/**
 * lib/marksCompliance.ts
 * ------------------------------------------------------------------
 * Pure classifier for the Marks-Entry Compliance view. For a given CA
 * label, it answers — per (teacher, class, subject) the teacher is
 * assigned — whether that CA's marks are COMPLETE / PARTIAL /
 * NOT-STARTED, so an academic admin can chase overdue teachers.
 *
 * Responsibility is resolved the authoritative way (same link the rest
 * of the app uses): a student's subject is owned by
 * student.subjectTeachers[subjectId]. Students taking a subject with no
 * recorded owner collapse into an 'Unassigned' group so the gap is
 * visible rather than hidden.
 *
 * The THREE edges that make this fiddly (all handled here):
 *   1. Sparse `ca` — Mark Entry OMITS blank months, so a month key may
 *      be absent entirely. Absent key = not entered.
 *   2. 'ABS' COUNTS as entered — an absent student is a deliberate mark,
 *      NOT a blank (isBlank('ABS') === false).
 *   3. CA, not exam — we look ONLY at entry.ca[caLabel]; the exam is a
 *      separate field with its own (term-final) deadline.
 *
 * Pure (no Firestore / React) so the numbers are testable and match the
 * rest of the app. The caller passes rosters + a getEntries() accessor.
 * ------------------------------------------------------------------ */
import { isBlank } from '@/lib/grading';
import type { Student, Subject, Teacher, SchoolClass, SubjectMarkEntry } from '@/types';

export type ComplianceStatus = 'complete' | 'partial' | 'not-started';

export interface ComplianceRow {
  key: string; // `${classId}|${subjectId}|${teacherId}`
  teacherId: string; // '' = unassigned
  teacherName: string;
  classId: string;
  className: string;
  subjectId: string;
  subjectName: string;
  total: number; // roster students for this teacher+class+subject
  entered: number; // of those, CA entered (incl. 'ABS')
  status: ComplianceStatus;
  /** Roster students still missing this CA (empty when complete), name-sorted. */
  missing: { id: string; name: string }[];
}

/**
 * A CA value counts as ENTERED when it is not blank. `isBlank` treats
 * null/undefined/'' as blank, so an omitted month or an empty cell is
 * "not entered", while a number OR the string 'ABS' counts as entered.
 */
export function caEntered(entry: SubjectMarkEntry | undefined, caLabel: string): boolean {
  if (!entry) return false;
  return !isBlank(entry.ca?.[caLabel]);
}

/** The exam mark counts as ENTERED when it is not blank (a number or 'ABS'). */
export function examEntered(entry: SubjectMarkEntry | undefined): boolean {
  if (!entry) return false;
  return !isBlank(entry.exam);
}

export interface BuildComplianceArgs {
  caLabel: string;
  students: Student[];
  classes: SchoolClass[];
  subjects: Subject[];
  teachers: Teacher[];
  /** reportMarks entries map for a (class, subject), or undefined if no doc. */
  getEntries: (classId: string, subjectId: string) => Record<string, SubjectMarkEntry> | undefined;
}

export type ComplianceInputs = Omit<BuildComplianceArgs, 'caLabel'>;

/** Shared core: classify every assigned (teacher, class, subject) using a
 *  per-entry "is this counted as entered?" predicate. buildCaCompliance and
 *  buildExamCompliance differ only in that predicate. */
function buildCompliance(
  args: ComplianceInputs,
  isEntered: (entry: SubjectMarkEntry | undefined) => boolean,
): ComplianceRow[] {
  const { students, classes, subjects, teachers, getEntries } = args;
  const classById = new Map(classes.map((c) => [c.id, c]));
  const subjectById = new Map(subjects.map((s) => [s.id, s]));
  const teacherById = new Map(teachers.map((t) => [t.id, t]));

  // Group roster students by (classId, subjectId, ownerTeacherId).
  const groups = new Map<string, { classId: string; subjectId: string; teacherId: string; roster: Student[] }>();
  for (const s of students) {
    const classId = s.currentClass;
    if (!classById.has(classId)) continue; // student not in a known class
    for (const subId of s.subjects ?? []) {
      if (!subjectById.has(subId)) continue; // unknown / stale subject id
      const owner = s.subjectTeachers?.[subId] ?? '';
      const key = `${classId}|${subId}|${owner}`;
      let g = groups.get(key);
      if (!g) {
        g = { classId, subjectId: subId, teacherId: owner, roster: [] };
        groups.set(key, g);
      }
      g.roster.push(s);
    }
  }

  const rows: ComplianceRow[] = [];
  for (const g of groups.values()) {
    const entries = getEntries(g.classId, g.subjectId);
    const missing: { id: string; name: string }[] = [];
    for (const s of g.roster) if (!isEntered(entries?.[s.id])) missing.push({ id: s.id, name: s.fullName || s.id });
    missing.sort((a, b) => a.name.localeCompare(b.name));
    const total = g.roster.length;
    const entered = total - missing.length;
    const status: ComplianceStatus = entered === 0 ? 'not-started' : entered >= total ? 'complete' : 'partial';
    const teacher = g.teacherId ? teacherById.get(g.teacherId) : undefined;
    const teacherName = teacher
      ? teacher.displayName || teacher.fullName
      : g.teacherId
        ? 'Unknown teacher'
        : 'Unassigned';
    rows.push({
      key: g.classId + '|' + g.subjectId + '|' + g.teacherId,
      teacherId: g.teacherId,
      teacherName,
      classId: g.classId,
      className: classById.get(g.classId)?.name ?? g.classId,
      subjectId: g.subjectId,
      subjectName: subjectById.get(g.subjectId)?.name ?? g.subjectId,
      total,
      entered,
      status,
      missing,
    });
  }
  return rows;
}

/** Classify every assigned (teacher, class, subject) for one CA label. */
export function buildCaCompliance(args: BuildComplianceArgs): ComplianceRow[] {
  const { caLabel } = args;
  return buildCompliance(args, (entry) => caEntered(entry, caLabel));
}

/** Classify every assigned (teacher, class, subject) by EXAM-mark entry.
 *  The caller passes ONLY exam-bearing classes (June cohort is excluded —
 *  they have no exam this term). */
export function buildExamCompliance(args: ComplianceInputs): ComplianceRow[] {
  return buildCompliance(args, examEntered);
}

/** Positive when the deadline is in the past (days overdue); 0 today; negative if future. */
export function daysOverdue(deadlineISO: string, todayISO: string): number {
  const d = Date.parse(deadlineISO + 'T00:00:00');
  const t = Date.parse(todayISO + 'T00:00:00');
  if (Number.isNaN(d) || Number.isNaN(t)) return 0;
  return Math.round((t - d) / 86_400_000);
}
